import { TutorBookingChangeProposal, TutorBookingChangeStatus } from '../models/TutorBookingChange';
import {
  bookingUtcToWallClock,
  parseBookingDisplayInstant,
  parseBookingProposalInstant,
  serializeBookingInstantForApi,
  serializeBookingWallClockForApi,
} from './bookingInstant';
import { parseWallClockInstant } from './wallClockInstant';

export interface TutorBookingScheduleLabels {
  dateLabel: string;
  timeLabel: string;
  durationLabel: string;
}

export type TutorBookingScheduleInstantSource = 'display' | 'proposal';

export interface FormatTutorBookingScheduleOptions {
  /** Booking display IANA zone (tutor → space → UTC). Used when `source` is `proposal`. */
  timezone?: string | null;
  /**
   * `display` — booking start/end wall clock (default).
   * `proposal` — UTC instants without `Z` (change-proposal old/new times).
   */
  source?: TutorBookingScheduleInstantSource;
}

export function formatTutorBookingScheduleLabels(
  startTime: string,
  endTime: string,
  locale: string,
  formatDuration: (minutes: number) => string,
  options?: FormatTutorBookingScheduleOptions,
): TutorBookingScheduleLabels {
  const asProposal = options?.source === 'proposal';
  const tz = options?.timezone?.trim() || '';
  const start = asProposal
    ? parseBookingProposalInstant(startTime)
    : parseBookingDisplayInstant(startTime);
  const end = asProposal
    ? parseBookingProposalInstant(endTime)
    : parseBookingDisplayInstant(endTime);
  const zoneOpts: Intl.DateTimeFormatOptions = asProposal && tz ? { timeZone: tz } : {};
  const dateLabel = start.toLocaleDateString(locale, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...zoneOpts,
  });
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...zoneOpts,
  };
  const startLabel = start.toLocaleTimeString(locale, timeOpts);
  const endLabel = end.toLocaleTimeString(locale, timeOpts);
  return {
    dateLabel,
    timeLabel: `${startLabel}–${endLabel}`,
    durationLabel: formatDuration(calculateBookingDurationMinutes(startTime, endTime)),
  };
}

/** Previous time text for inline strikethrough; includes date when the day also changed. */
export function getTutorBookingPreviousTimeLabel(
  previous: TutorBookingScheduleLabels,
  proposed: TutorBookingScheduleLabels,
): string {
  return previous.dateLabel === proposed.dateLabel
    ? previous.timeLabel
    : `${previous.dateLabel}, ${previous.timeLabel}`;
}

export function parseProposalUtcIso(iso: string): Date {
  return parseBookingProposalInstant(iso);
}

/**
 * Change-proposal instants are UTC LocalDateTime without `Z`.
 * Convert to a Date whose local fields match the lesson timezone.
 *
 * Booking read `startTime` / `endTime` are already wall clock in that timezone.
 * Seed the picker from those with `tutorBookingDisplayToPickerDate` instead.
 */
export function tutorBookingUtcToPickerDate(
  iso: string,
  timezone?: string | null,
): Date {
  return bookingUtcToWallClock(parseBookingProposalInstant(iso), timezone);
}

/** Booking list/detail start and end, already wall clock in the booking timezone. */
export function tutorBookingDisplayToPickerDate(iso: string): Date {
  return parseWallClockInstant(iso);
}

/** Display wall clock → UTC LocalDateTime stored by tutor change-proposal APIs. */
export function tutorBookingDisplayToUtcIso(iso: string, timezone?: string | null): string {
  return serializeTutorBookingPickerDate(tutorBookingDisplayToPickerDate(iso), timezone);
}

/** Serialize a picker wall-clock selection as the UTC LocalDateTime tutor APIs expect. */
export function serializeTutorBookingPickerDate(
  date: Date,
  timezone?: string | null,
): string {
  return serializeBookingWallClockForApi(date, timezone);
}

/** UTC proposal instant formatted in the booking timezone (or the browser zone). */
export function formatProposalDateTime(
  iso: string,
  locale: string,
  timezone?: string | null,
): string {
  const date = parseProposalUtcIso(iso);
  const tz = timezone?.trim() || '';
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(tz ? { timeZone: tz } : {}),
  });
}

export function proposalTimeChanged(proposal: TutorBookingChangeProposal): boolean {
  return parseProposalUtcIso(proposal.oldStartTime).getTime() !== parseProposalUtcIso(proposal.newStartTime).getTime();
}

export function calculateBookingDurationMinutes(startTime: string, endTime: string): number {
  const start = parseBookingDisplayInstant(startTime);
  const end = parseBookingDisplayInstant(endTime);
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
}

export function getProposalOldEndIso(
  proposal: TutorBookingChangeProposal,
  bookingStart: string,
  bookingEnd: string,
): string {
  const durationMinutes = calculateBookingDurationMinutes(bookingStart, bookingEnd);
  const oldStartMs = parseProposalUtcIso(proposal.oldStartTime).getTime();
  return serializeBookingInstantForApi(new Date(oldStartMs + durationMinutes * 60 * 1000));
}

export function getProposalNewEndIso(
  proposal: TutorBookingChangeProposal,
  bookingStart: string,
  bookingEnd: string,
): string {
  const syntheticOldEnd = getProposalOldEndIso(proposal, bookingStart, bookingEnd);
  const durationMinutes = calculateBookingDurationMinutes(proposal.oldStartTime, syntheticOldEnd);
  const newStartMs = parseProposalUtcIso(proposal.newStartTime).getTime();
  return serializeBookingInstantForApi(new Date(newStartMs + durationMinutes * 60 * 1000));
}

export function formatProposalDateTimeWithDuration(
  startIso: string,
  endIso: string,
  locale: string,
  formatDuration: (minutes: number) => string,
  timezone?: string | null,
): string {
  const minutes = calculateBookingDurationMinutes(startIso, endIso);
  return `${formatProposalDateTime(startIso, locale, timezone)} (${formatDuration(minutes)})`;
}

export function getTutorChangeProposalReason(proposal: TutorBookingChangeProposal): string | null {
  const candidates = [proposal.proposalReason, proposal.replyComment, proposal.cancelComment];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return null;
}

export function isTutorChangeProposalActionable(status: TutorBookingChangeStatus): boolean {
  return status === TutorBookingChangeStatus.PENDING_REPLY;
}

export function isTutorChangeProposalTerminal(status: TutorBookingChangeStatus): boolean {
  return (
    status === TutorBookingChangeStatus.ACCEPTED ||
    status === TutorBookingChangeStatus.DECLINED ||
    status === TutorBookingChangeStatus.CANCELLED
  );
}

export function getTutorChangeProposalStatusDescription(
  status: TutorBookingChangeStatus,
  t: (key: string, defaultValue: string) => string,
): string {
  switch (status) {
    case TutorBookingChangeStatus.PENDING_REPLY:
      return t('bookings.changeProposalStatus.pending_reply', 'Pending Reply');
    case TutorBookingChangeStatus.ACCEPTED:
      return t('bookings.changeProposalScheduleChip.accepted', 'Change accepted');
    case TutorBookingChangeStatus.DECLINED:
      return t('bookings.changeProposalScheduleChip.declined', 'Change declined');
    case TutorBookingChangeStatus.CANCELLED:
      return t('bookings.changeProposalScheduleChip.cancelled', 'Change cancelled');
    default:
      return status;
  }
}

/** Student-facing change proposal status labels on booking cards and detail. */
export function getUserTutorChangeProposalStatusLabel(
  status: TutorBookingChangeStatus,
  t: (key: string, defaultValue: string) => string,
): string {
  if (status === TutorBookingChangeStatus.PENDING_REPLY) {
    return t('userTutorBookings.changeProposal.pendingReplyStatus', 'Awaiting your reply');
  }
  return getTutorChangeProposalStatusDescription(status, t);
}

export function getTutorChangeProposalAutoAcceptAlert(
  status: TutorBookingChangeStatus,
  autoAccept: boolean | undefined,
  t: (key: string, defaultValue: string) => string,
): { severity: 'info' | 'success' | 'warning'; message: string } | null {
  if (!autoAccept) return null;

  switch (status) {
    case TutorBookingChangeStatus.PENDING_REPLY:
      return {
        severity: 'info',
        message: t(
          'userTutorBookings.changeProposal.autoAcceptPending',
          'If you accept, the lesson will be confirmed automatically at the new time.',
        ),
      };
    case TutorBookingChangeStatus.ACCEPTED:
      return {
        severity: 'success',
        message: t(
          'userTutorBookings.changeProposal.autoAcceptAccepted',
          'When you accepted, the booking was confirmed automatically, as the tutor requested.',
        ),
      };
    case TutorBookingChangeStatus.DECLINED:
      return {
        severity: 'warning',
        message: t(
          'userTutorBookings.changeProposal.autoAcceptDeclined',
          'The tutor had automatic confirmation on accept turned on—you declined, so that did not apply.',
        ),
      };
    case TutorBookingChangeStatus.CANCELLED:
      return {
        severity: 'info',
        message: t(
          'userTutorBookings.changeProposal.autoAcceptClosed',
          'This proposal included automatic confirmation if accepted; it no longer applies.',
        ),
      };
    default:
      return null;
  }
}

export function getTutorChangeProposalStatusColor(
  status: TutorBookingChangeStatus,
): 'default' | 'warning' | 'success' | 'error' {
  switch (status) {
    case TutorBookingChangeStatus.PENDING_REPLY:
      return 'warning';
    case TutorBookingChangeStatus.ACCEPTED:
      return 'success';
    case TutorBookingChangeStatus.DECLINED:
      return 'error';
    case TutorBookingChangeStatus.CANCELLED:
      return 'default';
    default:
      return 'default';
  }
}
